import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { sumItems, sumRemaining, remainingFor } from './productionUtils';
import {
  groupCustomerCount,
  isInteractiveTarget,
  DueDate,
  WoProgress,
  ShortBadge,
  BlockPacking,
  StockBalance,
  DefectCodeSelect,
  RejectQuantityInput,
  ReportQuantityInput,
  ToReportHint
} from './WorkOrderRowParts';

// Sort indicator for a sortable column header
const SortIndicator = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return <span className="sort-icon inactive" aria-hidden="true"><ChevronUp size={12} /></span>;
  return sortDirection === 'asc'
    ? <span className="sort-icon active" aria-hidden="true"><ChevronUp size={12} /></span>
    : <span className="sort-icon active" aria-hidden="true"><ChevronDown size={12} /></span>;
};

// Column header that sorts on click, announced to screen readers via aria-sort
const SortableHeader = ({ field, className, children, sortField, sortDirection, onSort }) => (
  <th
    className={`${className} sortable-header`}
    aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
  >
    <button type="button" className="sort-header-btn" onClick={() => onSort(field)}>
      {children} <SortIndicator field={field} sortField={sortField} sortDirection={sortDirection} />
    </button>
    {/* Chrome leaves buttons out of the header it repeats on each printed page */}
    <span className="print-only" aria-hidden="true">{children}</span>
  </th>
);

// Work orders table - desktop layout (swapped for WorkOrdersCards at 900px and below via
// CSS, see CARD_LAYOUT_QUERY). The page scrolls; the header row sticks to the top.
// Customer / due date columns are hidden when every visible row shares the value; the
// parent then shows it once above the table.
const WorkOrdersTable = ({
  displayRows,
  selectedItems,
  quantities,
  rejectQuantities,
  defectCodes,
  defectCodeSelections,
  isShort,
  partBalances,
  stockFetchedAt,
  hideCustomerCol,
  hideDateCol,
  changedSerials,
  sortField,
  sortDirection,
  onSort,
  onSelectItem,
  onSelectGroup,
  onQuantityChange,
  onRejectQuantityChange,
  onDefectCodeChange
}) => {
  const sortProps = { sortField, sortDirection, onSort };

  return (
    <div className="work-orders-table-container">
      <table className="work-orders-table">
        <thead>
          <tr>
            <th className="col-checkbox"><span className="sr-only">Select</span></th>
            <SortableHeader field="workOrder" className="col-serial" {...sortProps}>Work Order</SortableHeader>
            <SortableHeader field="partDescription" className="col-part" {...sortProps}>Part Description</SortableHeader>
            {!hideCustomerCol && (
              <SortableHeader field="customer" className="col-customer" {...sortProps}>Customer</SortableHeader>
            )}
            {!hideDateCol && (
              <SortableHeader field="dueDate" className="col-date" {...sortProps}>Due Date</SortableHeader>
            )}
            <SortableHeader field="remaining" className="col-progress" {...sortProps}>Reported / Planned</SortableHeader>
            <th className="col-reject">Rejects</th>
            <th className="col-report">To Report</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row) => {
            if (row.type === 'colour') {
              const colourSelected = row.wos.every(w => selectedItems.has(w.serialName));
              return (
                <tr key={`colour-${row.key}`} className="colour-group-row">
                  <td className="col-checkbox">
                    <input
                      type="checkbox"
                      checked={colourSelected}
                      onChange={() => onSelectGroup(row.wos)}
                      title="Select all work orders in this colour"
                      aria-label={`Select all ${row.wos.length} work orders in ${row.label}`}
                    />
                  </td>
                  <td colSpan={5 + (hideCustomerCol ? 0 : 1) + (hideDateCol ? 0 : 1)} className="colour-group-cell">
                    <div className="colour-group-info">
                      <span className="colour-group-label">{row.label}</span>
                      <span className="colour-group-summary">
                        {sumRemaining(row.wos)} to make &middot; {row.wos.length} {row.wos.length === 1 ? 'work order' : 'work orders'}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            }
            if (row.type === 'group') {
              const groupSelected = row.wos.every(w => selectedItems.has(w.serialName));
              const first = row.wos[0];
              const groupCustomers = groupCustomerCount(row.wos);
              return (
                <tr key={`group-${row.key}`} className="sku-group-row">
                  <td className="col-checkbox">
                    <input
                      type="checkbox"
                      checked={groupSelected}
                      onChange={() => onSelectGroup(row.wos)}
                      title="Select all work orders for this SKU"
                      aria-label={`Select all ${row.wos.length} work orders for ${first.partName || first.partDescription}`}
                    />
                  </td>
                  <td colSpan={5 + (hideCustomerCol ? 0 : 1) + (hideDateCol ? 0 : 1)} className="sku-group-cell">
                    <div className="sku-group-info">
                      <span className="sku-group-desc">{first.partDescription || first.partName}</span>
                      {first.partName && first.partDescription && (
                        <span className="sku-group-code">{first.partName}</span>
                      )}
                      <span className="sku-group-summary">
                        {row.wos.length} work orders
                        {groupCustomers > 1 && <> &middot; {groupCustomers} customers</>}
                        {' '}&middot; {sumItems(row.wos)} items
                      </span>
                    </div>
                  </td>
                </tr>
              );
            }
            const wo = row.wo;
            const reportedQty = wo.reportedQuantity || 0;
            const plannedQty = Number(wo.quantity) || 0;
            const remainingQty = remainingFor(wo);
            return (
              <tr
                key={wo.serialName}
                className={`work-order-row ${row.grouped ? 'sku-grouped' : ''} ${selectedItems.has(wo.serialName) ? 'selected' : ''} ${changedSerials.has(wo.serialName) ? 'wo-changed' : ''}`}
                onClick={(e) => { if (!isInteractiveTarget(e.target)) onSelectItem(wo.serialName); }}
              >
                <td className="col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(wo.serialName)}
                    onChange={() => onSelectItem(wo.serialName)}
                    aria-label={`Select ${wo.serialName}`}
                  />
                </td>
                <td className="col-serial">{wo.serialName}</td>
                <td className="col-part" title={`${wo.partName} - ${wo.processDescription}`}>
                  <div className="part-info">
                    <span className="part-description">
                      {wo.partDescription || wo.partName}
                      {isShort(wo) && <ShortBadge />}
                    </span>
                    {wo.partName && wo.partDescription && (
                      <span className="part-name">{wo.partName}</span>
                    )}
                    <BlockPacking wo={wo} />
                    <StockBalance wo={wo} balances={partBalances} fetchedAt={stockFetchedAt} />
                  </div>
                </td>
                {!hideCustomerCol && (
                  <td className="col-customer">
                    <div className="customer-info">
                      <span className="customer-name">{wo.customerName || wo.customerCode || '-'}</span>
                      {wo.salesOrder && <span className="sales-order">{wo.salesOrder}</span>}
                    </div>
                  </td>
                )}
                {!hideDateCol && (
                  <td className="col-date"><DueDate date={wo.endDate || wo.plannedDate} /></td>
                )}
                <td className="col-progress">
                  <WoProgress
                    reported={reportedQty}
                    planned={plannedQty}
                    remaining={remainingQty}
                  />
                </td>
                <td className="col-reject">
                  <RejectQuantityInput
                    serialName={wo.serialName}
                    value={rejectQuantities[wo.serialName]}
                    onChange={onRejectQuantityChange}
                  />
                  <DefectCodeSelect
                    serialName={wo.serialName}
                    defectCodes={defectCodes}
                    rejectQuantity={rejectQuantities[wo.serialName]}
                    value={defectCodeSelections[wo.serialName]}
                    onChange={onDefectCodeChange}
                  />
                </td>
                <td className="col-report">
                  <ReportQuantityInput
                    wo={wo}
                    value={quantities[wo.serialName]}
                    onChange={onQuantityChange}
                  />
                  <ToReportHint wo={wo} value={quantities[wo.serialName]} onQuantityChange={onQuantityChange} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default WorkOrdersTable;
